import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Optional,
  Output,
  QueryList,
  SkipSelf,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CalloutComponent } from '@rivet-health/design-system';
import { ClickNavigationMenuItem } from '@rivethealth/data/analytics/account-analytics-events';
import { reverse } from 'lodash';
import { filter } from 'rxjs';
import { AccountAnalyticsService } from '../../app/account/account-analytics.service';
import { AccountState } from '../../app/account/account.state';
import { HookDirective } from '../core/hook';
import { useMemo, useState, useSubscriptionLatest } from '../core/hooks';

@Component({
  selector: 'top-nav-item',
  templateUrl: './top-nav-item.component.html',
  styleUrls: ['./top-nav-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopNavItemComponent extends HookDirective {
  constructor(
    @Optional()
    @SkipSelf()
    private readonly parent: TopNavItemComponent,
    @Optional()
    private readonly account: AccountState,
    private readonly router: Router,
    @Optional()
    private readonly analyticsService: AccountAnalyticsService,
    _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  @Input()
  title?: string;

  @Input()
  url?: string | string[];

  @Input()
  newTab: boolean = false;

  @Input()
  menuPreferredPosition?: CalloutComponent.AnchoredPosition;

  @Input()
  locked: boolean = false;

  @Input()
  currentRequireExact: boolean = false;

  // In one single case, the responsive menu button, Angular is unable to query
  // child TopNavItemComponents, even though we pass { descendarnts: true }.
  // (Even more oddly, those same child nav items are able to inject the
  // responsive button as their parent into their constructor. :shrug_emoji:)
  // So for that one case, we help Angular out and pass true to hasChildren to
  // allow the menu to open.
  @Input()
  hasChildren: boolean = false;

  @ContentChildren(TopNavItemComponent, { descendants: true })
  children!: QueryList<TopNavItemComponent>;

  @Output()
  click = new EventEmitter<MouseEvent>();

  get _() {
    const [open, setOpen] = useState(false);

    const topLevel = !this.parent;
    const hasChildren = !!this.children?.length || this.hasChildren;

    const closeTree = () => {
      setOpen(false);
      this.parent?._.closeTree();
    };

    const latestNavigationEnd = useSubscriptionLatest(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      [],
    );
    const current = useMemo(() => {
      if (this.children.some(child => child._.current)) return true;
      if (!this.url) return false;
      const tree = this.router.createUrlTree(
        this.account
          ? [
              'accounts',
              this.account.accountId,
              ...(typeof this.url === 'string'
                ? this.url.split('/')
                : this.url),
            ]
          : [this.url],
      );
      return this.router.isActive(tree, {
        paths: this.currentRequireExact ? 'exact' : 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      });
    }, [
      this.url,
      this.children,
      this.currentRequireExact,
      latestNavigationEnd,
    ]);

    const click = (event: MouseEvent) => {
      if (this.analyticsService) {
        const noTitle = '(custom content)';
        const ancestorTitles: string[] = [];
        let parent = this.parent;
        while (parent) {
          ancestorTitles.push(parent.title || noTitle);
          parent = parent.parent;
        }
        this.analyticsService.logEvent(ClickNavigationMenuItem, {
          'top level': topLevel,
          title: this.title || noTitle,
          'ancestor titles': ancestorTitles.length
            ? reverse(ancestorTitles).join(' > ')
            : null,
          locked: this.locked,
        });
      }
      if (this.locked) return;
      this.click.emit(event);
    };

    const isFullUrl = this.url
      ? typeof this.url === 'string' && this.url.startsWith('http')
      : false;

    return {
      click,
      closeTree,
      current,
      hasChildren,
      isFullUrl,
      open,
      setOpen,
      topLevel,
    };
  }
}
